import { Center, Text3D } from '@react-three/drei'
import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useParams } from 'wouter'
import { audioVolumeAtom, clientAtom, languageAtom, minigameCountdownAtom, minigameGamePhaseAtom, minigameMessageAtom, minigameOpponentShakeAtom, minigameScoresAtom, minigameStarPopAtom, minigameStarsAtom, minigameStateAtom, minigameTimeRemainingAtom, teamsAtom } from '../GlobalState'
import useStarRoll from '../shader/starRoll/StarRoll'
import StarsPatterns2Shader from '../shader/starsPatterns2/StarsPatterns2Shader'
import { getSocket } from '../socket'
import useSoundEffectsPlayer from '../soundPlayers/useSoundEffectsPlayer'
import { getNameFont } from '../logicHelpers/helpers'
import MinigameStar2 from './MinigameStar2'
import YutBonus from '../components/YutBonus'
import translations from '../dictionaries/translations'
import { trackEvent } from '../api'

const socket = getSocket()
const font = '/fonts/Luckiest Guy_Regular.json'
const SHAKY_STAR_POP_SOUND_PATH = '/sounds/effects/shaky-star-pop.mp3'
const SET_DUL_HANA_SOUND_PATH = '/sounds/effects/set-dul-hana.mp3'
const BUBBLE_POP_VOLUME_MULTIPLIER = 1.5
const BIG_BUBBLE_POP_SOUND_PATH = '/sounds/effects/big-bubble-pop.mp3'
const BIG_BUBBLE_POP_VOLUME_MULTIPLIER = 2
const MOON_OPPONENT_STAR_OPACITY = 0.05
const SPECIAL_STAR_SHAKE_INTENSITY = 1.05

function flatten(stars) {
	return Array.isArray(stars) ? (Array.isArray(stars[0]) ? stars.flat() : stars) : []
}

function starPosition(index, star) {
	if (Number.isFinite(star?.visualX) && Number.isFinite(star?.visualZ)) return [star.visualX, 0.1, star.visualZ]
	if (Number.isFinite(star?.x) && Number.isFinite(star?.z)) return [star.x, 0.1, star.z]
	return [((index % 5) - 2) * 2.4, 0.1, (Math.floor(index / 5)) * 2.4]
}

function Button({ children, position, active, setActive, onClick }) {
	function enter(event) { event.stopPropagation(); setActive(true); document.body.style.cursor = 'pointer' }
	function leave(event) { event.stopPropagation(); setActive(false); document.body.style.cursor = 'default' }
	return <group position={position}>
		<mesh><boxGeometry args={[3, 0.04, 1.4]} /><meshStandardMaterial color={active ? 'green' : 'yellow'} /></mesh>
		<mesh position={[0, 0.025, 0]} onPointerEnter={enter} onPointerLeave={leave} onPointerMove={enter} onPointerDown={onClick}><boxGeometry args={[2.6, 0.05, 0.9]} /><meshStandardMaterial transparent opacity={0} /></mesh>
		<Center position={[0, 0.05, 0]}><Text3D font={getNameFont(String(children), font)} size={String(children).length > 10 ? 0.28 : 0.42} height={0.01} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color='black' />{children}</Text3D></Center>
	</group>
}

function Label({ children, color, position, size = 0.45, textFont = font }) {
	return <Center position={position}><Text3D font={getNameFont(String(children), textFont)} size={size} height={0.01} position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color={color} />{children}</Text3D></Center>
}

function ScorePopup({ score, team, position }) {
	return <Center position={[position.x, 0.5, position.z]}><Text3D font={font} size={0.75} height={0.01} rotation={[-Math.PI / 2, 0, 0]}><meshStandardMaterial color={team === 0 ? 'red' : 'turquoise'} />{score}</Text3D></Center>
}

function ResultTeamColumn({ team, teamName, players, playerScores, winnerTeam, labels, language }) {
	const color = team === 0 ? 'red' : 'turquoise'
	const positionX = team === 0 ? -3.4 : 2.2
	const isWinner = winnerTeam === team
	const teamScore = players.reduce((total, player) => total + (Number(playerScores?.[player.id]) || 0), 0)
	return <group position={[positionX, 0, -0.8]}>
		<Label color={color} size={0.5} position={[0, 0.5, 0]}>{`${teamName} ${teamScore}`}</Label>
		{players.map((player, index) => <Label
			key={player.id}
			color={color}
			size={0.35}
			position={[0, 0.5, 0.9 + index * 0.65]}
			textFont={getNameFont(player.name)}
		>{`${player.name} ${playerScores?.[player.id] ?? 0}`}</Label>)}
		{isWinner && <>
			<YutBonus position={[0.8, 0.2, 1.6 + players.length * 0.65]} scale={0.7} alwaysShow enableClick={false} modelOnly />
			<Label color='yellow' size={0.28} position={[0.45, 0.5, 2.95 + players.length * 0.65]}>{labels.freeYut[language]}</Label>
		</>}
	</group>
}

export default function MinigameScene2() {
	const client = useAtomValue(clientAtom)
	const language = useAtomValue(languageAtom)
	const audioVolume = useAtomValue(audioVolumeAtom)
	const teams = useAtomValue(teamsAtom)
	const phase = useAtomValue(minigameGamePhaseAtom)
	const countdown = useAtomValue(minigameCountdownAtom)
	const scores = useAtomValue(minigameScoresAtom)
	const serverStars = useAtomValue(minigameStarsAtom)
	const timeRemaining = useAtomValue(minigameTimeRemainingAtom)
	const message = useAtomValue(minigameMessageAtom)
	const opponentShake = useAtomValue(minigameOpponentShakeAtom)
	const starPop = useAtomValue(minigameStarPopAtom)
	const minigameState = useAtomValue(minigameStateAtom)
	const setStars = useSetAtom(minigameStarsAtom)
	const [RollStar] = useStarRoll()
	const { playSoundEffect } = useSoundEffectsPlayer()
	const handledStarPopId = useRef(null)
	const demoLoadedRef = useRef(false)
	const hasPlayedCountdown = useRef(false)
	const [flashOn, setFlashOn] = useState(true)
	const [hover, setHover] = useState('')
	const [collectedScore, setCollectedScore] = useState(null)
	const [continueSeconds, setContinueSeconds] = useState(10)
	const [effectNow, setEffectNow] = useState(Date.now())
	const { id } = useParams()
	const roomId = id?.toUpperCase()
	const demoStars = useMemo(() => Array.from({ length: 15 }, (_, index) => ({
		index,
		type: index === 14 ? 'moon' : index >= 11 ? 'bigStar' : 'star',
	})), [])
	const stars = useMemo(() => {
		const source = phase === 'start' && flatten(serverStars).length === 0 ? demoStars : serverStars
		return flatten(source)
	}, [demoStars, phase, serverStars])

	useEffect(() => {
		if (phase !== 'playing' || timeRemaining > 5 || timeRemaining <= 0) { setFlashOn(true); return undefined }
		const intervalId = window.setInterval(() => setFlashOn((value) => !value), 250)
		return () => window.clearInterval(intervalId)
	}, [phase, timeRemaining])

	useEffect(() => () => { document.body.style.cursor = 'default' }, [])

	useEffect(() => {
		const expiresAt = Number(opponentShake.expiresAt)
		if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return undefined
		const timeoutId = window.setTimeout(() => setEffectNow(Date.now()), expiresAt - Date.now() + 20)
		return () => window.clearTimeout(timeoutId)
	}, [opponentShake.expiresAt])

	useEffect(() => {
		if (phase !== 'countdown') {
			hasPlayedCountdown.current = false
			return
		}
		if (countdown === 3 && !hasPlayedCountdown.current) {
			hasPlayedCountdown.current = true
			playSoundEffect(SET_DUL_HANA_SOUND_PATH, audioVolume)
		}
	}, [audioVolume, countdown, phase, playSoundEffect])

	useEffect(() => {
		if (phase !== 'finished') {
			setContinueSeconds(10)
			return undefined
		}
		if (continueSeconds === 0) {
			emit('minigameReturnToGame')
			return undefined
		}
		const timeoutId = window.setTimeout(() => setContinueSeconds((seconds) => seconds - 1), 1000)
		return () => window.clearTimeout(timeoutId)
	}, [continueSeconds, phase])

	useEffect(() => {
		if (!starPop?.position || starPop.id === handledStarPopId.current) return
		handledStarPopId.current = starPop.id
		const isShakenClient = client.socketId === starPop.poppedBySocketId || (
			(client.team === 0 || client.team === 1) && client.team === starPop.shakyTeam
		)
		const soundPath = isShakenClient ? SHAKY_STAR_POP_SOUND_PATH : starPop.soundPath
		const volumeMultiplier = soundPath === BIG_BUBBLE_POP_SOUND_PATH
			? BUBBLE_POP_VOLUME_MULTIPLIER * BIG_BUBBLE_POP_VOLUME_MULTIPLIER
			: BUBBLE_POP_VOLUME_MULTIPLIER
		playSoundEffect(
			soundPath,
			audioVolume * volumeMultiplier
		)
		setCollectedScore({ score: starPop.score, team: starPop.team, position: starPop.position })
		const color = starPop.team === 0 ? new THREE.Color('red') : new THREE.Color('turquoise')
		const origin = new THREE.Vector3(starPop.position.x, starPop.position.y, starPop.position.z)
		for (let burst = 0; burst < 6; burst += 1) {
			RollStar({ position: origin, rotation: new THREE.Vector3(0, burst * Math.PI / 3, 0), omitFactor: 10, size: 0.0008, scale: 0.24 + Math.random() * 0.16, color, duration: 1, speedX: 3 + Math.random() * 2, speedY: 1.5 + Math.random() * 1.5, brightness: 0.1 })
		}
		const timeoutId = window.setTimeout(() => setCollectedScore(null), 1000)
		return () => window.clearTimeout(timeoutId)
	}, [RollStar, audioVolume, client.team, playSoundEffect, starPop])

	function emit(event) { if (roomId) socket.emit(event, { roomId }) }

	function collectStar(event, index, star) {
		if (!roomId || !star || (phase !== 'start' && phase !== 'playing')) return
		event.stopPropagation()
		setStars((current) => { const next = flatten(current).slice(); next[index] = null; return next })
		socket.emit('minigameCollectStar', { roomId, position: index })
	}

	const active = phase === 'playing' || phase === 'countdown'
	const timerColor = timeRemaining > 15 ? 'limegreen' : timeRemaining > 5 ? 'yellow' : flashOn ? 'red' : '#5c0000'
	const opponentEffectActive = Number(client.team) === Number(opponentShake.team) && effectNow < (opponentShake.expiresAt ?? 0)
	const starsShaken = opponentEffectActive && opponentShake.type === 'purpleStar'
	const starsDimmed = opponentEffectActive && opponentShake.type === 'moon'
	const shakeUntil = starsShaken
		? opponentShake.expiresAt ?? 0
		: 0
	const resultPlayers = Object.values(minigameState.players ?? {})
	const resultTeams = [0, 1].map((team) => {
		const minigamePlayers = resultPlayers.filter((player) => player.team === team)
		if (minigamePlayers.length > 0) return minigamePlayers
		return (teams[team]?.players ?? []).map((player) => ({
			id: String(player._id ?? player.id),
			name: player.name,
			team,
			type: player.type
		}))
	})
	const resultWinnerTeam = Number.isInteger(minigameState.winnerTeam)
		? minigameState.winnerTeam
		: null
	const text = translations.minigames.twentyFingers
	const labels = translations.minigames.labels
	const resultMessage = resultWinnerTeam === null ? message : `${teams[resultWinnerTeam]?.name || (resultWinnerTeam === 0 ? labels.rockets[language] : labels.ufos[language])} ${labels.wins[language]}`
	const playerScores = minigameState.playerScores ?? {}
	return <>
		<group name='background'>
			<StarsPatterns2Shader count={10000} texturePath='/textures/particles/3.png' />
			<StarsPatterns2Shader count={10000} size={2} texturePath='/textures/particles/6.png' />
		</group>
		{phase !== 'finished' && <group name='minigame-board'>
			{stars.map((star, index) => star && <MinigameStar2 key={index} index={index} star={star} position={starPosition(index, star)} shakeUntil={shakeUntil} shakeIntensity={SPECIAL_STAR_SHAKE_INTENSITY} opacity={starsDimmed ? MOON_OPPONENT_STAR_OPACITY : 1} moonOpacity={starsDimmed ? MOON_OPPONENT_STAR_OPACITY : 1} onClick={(event) => collectStar(event, index, star)} />)}
			{collectedScore && <ScorePopup score={collectedScore.score} team={collectedScore.team} position={collectedScore.position} />}
			{phase === 'start' && <>
				<Center position={[0, 0.5, -10.3]}>
					<Label color='limegreen' size={0.62} position={[0, 0, 0]}>{text.title[language]}</Label>
				</Center>
				<Center position={[0, 0.5, -9]}>
					<Label color='yellow' size={0.32} position={[0, 0, 0]}>{labels.howToPlay[language]}</Label>
				</Center>
				<Center position={[0, 0.5, -8.1]}>
					<Label color='white' size={0.3} position={[0, 0, 0]}>{text.rule[language]}</Label>
				</Center>
				<Button position={[2.15, 0, 8.4]} active={hover === 'start'} setActive={(value) => setHover(value ? 'start' : '')} onClick={(event) => { event.stopPropagation(); setCollectedScore(null); emit('minigameStartGame'); trackEvent({ surface: 'minigame', action: 'minigame_started', category: 'gameplay', metadata: { type: 'stars' } }) }}>{translations.minigames.actions.start[language]}</Button>
				<Button position={[-2.15, 0, 8.4]} active={hover === 'back'} setActive={(value) => setHover(value ? 'back' : '')} onClick={(event) => { event.stopPropagation(); emit('minigameReturnToGame'); trackEvent({ surface: 'minigame', action: 'exit', category: 'ui', metadata: { type: 'stars' } }) }}>{translations.minigames.actions.back[language]}</Button>
			</>}
			{phase === 'countdown' && <Label color='yellow' size={1.8} position={[-0.55, 0.6, 1.5]}>{`${countdown ?? ''}...`}</Label>}
		</group>}
		{active && <><Label color='red' size={0.36} position={[-5.4, 0.5, -10.4]}>{`${labels.rockets[language]} ${scores[0]}`}</Label><Label color='turquoise' size={0.36} position={[-1.7, 0.5, -10.4]}>{`${labels.ufos[language]} ${scores[1]}`}</Label><Label color={timerColor} position={[3.45, 0.5, -10.55]}>{`${timeRemaining}s`}</Label>{message && <Label color='yellow' size={0.7} position={[-2, 0.5, 0.4]}>{message}</Label>}</>}
		{phase === 'finished' && <>
			<Label color='yellow' size={0.85} position={[-3.1, 0.5, -4.5]}>{resultMessage || labels.timeUp[language]}</Label>
			<ResultTeamColumn team={0} teamName={teams[0]?.name || labels.rockets[language]} players={resultTeams[0]} playerScores={playerScores} winnerTeam={resultWinnerTeam} labels={labels} language={language} />
			<ResultTeamColumn team={1} teamName={teams[1]?.name || labels.ufos[language]} players={resultTeams[1]} playerScores={playerScores} winnerTeam={resultWinnerTeam} labels={labels} language={language} />
			<Button position={[0, 0, 8.4]} active={hover === 'continue'} setActive={(value) => setHover(value ? 'continue' : '')} onClick={(event) => { event.stopPropagation(); emit('minigameReturnToGame') }}>{`${translations.minigames.actions.continue[language]} ${continueSeconds}`}</Button>
		</>}
	</>
}